import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ArrowLeftRight, BellRing, CheckCheck, Clock, Gauge, WifiOff } from "lucide-react";
import { useNavigate } from "react-router-dom";

export const NotificationsPanel = ({ open, onOpenChange, onChanged }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/notifications");
      setItems(r.data.notifications || []);
    } catch (e) { console.warn("[apex] ignored:", e); }
    setLoading(false);
  };

  useEffect(() => { if (open) load(); }, [open]);

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    load(); onChanged?.();
  };
  const markAll = async () => {
    await api.post(`/notifications/read-all`);
    load(); onChanged?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="notif-panel" className="bg-slate-950 border-slate-800 max-w-md p-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <BellRing className="w-4 h-4 text-cyan-300" />
            <DialogTitle className="font-display">Notifications</DialogTitle>
            {items.some((n) => !n.read) && (
              <button
                data-testid="notif-mark-all"
                onClick={markAll}
                className="ml-auto text-xs text-cyan-300 hover:text-cyan-200 flex items-center gap-1"
              >
                <CheckCheck className="w-3.5 h-3.5" /> Mark all read
              </button>
            )}
          </div>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto">
          {loading && <div className="p-6 text-sm text-slate-400">Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="notif-empty">
              No notifications yet. Arm an alert on any <span className="text-cyan-300">Wait</span> signal and we'll ping you when the setup changes.
            </div>
          )}
          {items.map((n) => (
            <button
              key={n.id}
              data-testid={`notif-${n.id}`}
              onClick={() => {
                markRead(n.id);
                onOpenChange(false);
                if (n.link) nav(n.link);
                else if (n.coin_id) nav(`/coin/${n.coin_id}`);
              }}
              className={`w-full text-left px-4 py-3 border-b border-slate-900 hover:bg-slate-800/40 flex gap-3 items-start ${n.read ? "opacity-70" : ""}`}
            >
              <div className={`w-8 h-8 rounded-lg border flex items-center justify-center shrink-0 ${n.kind === "feed_outage" ? (n.state === "down" ? "bg-rose-500/10 border-rose-900/50" : "bg-emerald-500/10 border-emerald-900/50") : n.kind === "paper_exit_executed" ? "bg-amber-500/10 border-amber-900/50" : ["prediction_milestone", "prediction_digest"].includes(n.kind) ? "bg-emerald-500/10 border-emerald-900/50" : "bg-cyan-500/10 border-cyan-900/50"}`}>
                {n.kind === "feed_outage" ? <WifiOff className={`w-4 h-4 ${n.state === "down" ? "text-rose-300" : "text-emerald-300"}`} data-testid={`notif-feed-icon-${n.id}`} /> : n.kind === "paper_exit_executed" ? <ArrowLeftRight className="w-4 h-4 text-amber-300" data-testid={`notif-exit-icon-${n.id}`} /> : ["prediction_milestone", "prediction_digest"].includes(n.kind) ? <Gauge className="w-4 h-4 text-emerald-300" data-testid={`notif-milestone-icon-${n.id}`} /> : <Clock className="w-4 h-4 text-cyan-300" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-100 truncate">{n.title}</div>
                <div className="text-xs text-slate-400 line-clamp-2">{n.body}</div>
                <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                  {new Date(n.created_at).toLocaleString()}
                  {n.push_status?.state === "sent" && <span className="text-emerald-400" data-testid={`notif-push-sent-${n.id}`}>· Sent to phone</span>}
                  {n.push_status?.state === "failed" && <span className="text-red-400" data-testid={`notif-push-failed-${n.id}`}>· Phone push failed</span>}
                </div>
              </div>
              {!n.read && <span className="w-2 h-2 rounded-full bg-cyan-400 shrink-0 mt-2" />}
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
